import { CompanyScoringState, OrganisationAssignmentDto } from 'models/dtoTypes';
import { styled } from '@mui/material/styles';
import { getSustainabilityScoreColor } from 'kosmosConfig';
import SmartGovernanceVerificationLogo from 'components/SmartGovernanceVerificationLogo';
// import { dateAdd } from 'utils/date';
// import UpdateIcon from '@mui/icons-material/Update';

const PREFIX = 'SmartGovernanceWidget';

const classes = {
  scoreContainer: `${PREFIX}-scoreContainer`,
  icon: `${PREFIX}-icon`,
  active: `${PREFIX}-active`,
  inactive: `${PREFIX}-inactive`,
};

const Root = styled('div')(() => ({
  cursor: 'pointer',
  backgroundColor: 'white',
  color: 'black',
  width: 150,
  height: 80,
  padding: 0,
  margin: 'auto',
  [`& .${classes.scoreContainer}`]: {
    height: '100%',
    color: 'white',
  },
  [`& .${classes.active}`]: {
    height: '100%',
    border: '2px solid #8d8d8d',
  },
  [`& .${classes.inactive}`]: {
    height: '100%',
    border: '2px solid #e8edf2',
  },
  ['& img']: {
    height: 76,
  }
}));

interface Props {
  kosmosCompanyId: string;
  smartGovernanceAssignment?: OrganisationAssignmentDto;
  kosmosCompanyScoringState?: CompanyScoringState;
  onClickCompleted?: (id: string) => void;
  isDemo?: boolean;
}

export enum ESmartGovernanceStatus {
  notStarted = 'Not Started',
  sent = 'Sent',
  accepted = 'Accepted',
  inProgress = 'In Progress',
  completed = 'Completed',
  assignError = 'Assign Error',
  scoreError = 'Score Error'
}

export default function SmartGovernanceWidget(props: Props): JSX.Element {
  const {
    kosmosCompanyId,
    smartGovernanceAssignment,
    isDemo,
    kosmosCompanyScoringState,
    onClickCompleted,
  } = props;

  let smartGovernanceScore = 0;

  if (isDemo) {
    // hardcode sg score for demo SG
    smartGovernanceScore = 540;
  } else {
    const manualCompany = smartGovernanceAssignment?.manualCompany;
    smartGovernanceScore = manualCompany?.esgScore || 0;
  }

  const getSmartGovernanceStatus = (): ESmartGovernanceStatus => {
    if (!smartGovernanceAssignment) {
      return ESmartGovernanceStatus.notStarted;
    } else if (!smartGovernanceAssignment.isAccepted) {
      return ESmartGovernanceStatus.sent;
    } else if (!smartGovernanceAssignment.manualCompanyId) {
      return ESmartGovernanceStatus.assignError;
    } else {
      const scoringState = smartGovernanceAssignment?.manualCompany?.scoringState ?? null;
      if (scoringState === CompanyScoringState.Completed) {
        return ESmartGovernanceStatus.completed;
      } else if (scoringState === CompanyScoringState.InProgress) {
        return ESmartGovernanceStatus.inProgress;
      } else if (scoringState === CompanyScoringState.Error) {
        return ESmartGovernanceStatus.scoreError;
      }
      return ESmartGovernanceStatus.accepted;
    }
  };

  const renderInProgress = () => {
    return (
      <div
        className={classes.active}
        onClick={handleClickInProgress}
      >
        <SmartGovernanceVerificationLogo
          active={true}
        />
      </div>
    );
  };

  const handleCreateNewSmartGovernance = () => {
    if (kosmosCompanyId && kosmosCompanyScoringState === CompanyScoringState.Completed) {
      window.open(`/companies/${kosmosCompanyId}/sg_assign`, '_blank');
      return;
    } else {
      alert('Automated score is not completed yet.');
    }
  };

  const handleClickInProgress = () => {
    return;
  };

  const renderNotStarted = () => {
    return (
      <div
        className={classes.inactive}
        onClick={handleCreateNewSmartGovernance}
      >
        <SmartGovernanceVerificationLogo
          active={false}
        />
      </div>
    );
  };

  const renderReadonly = () => {
    return (
      <div
        className={classes.inactive}
      >
        <SmartGovernanceVerificationLogo
          active={false}
        />
      </div>
    );
  };

  const handleClickSmartGovernance = () => {
    if (onClickCompleted) {
      onClickCompleted(kosmosCompanyId);
    }
  };

  const renderCompleted = () => {
    const color = getSustainabilityScoreColor(smartGovernanceScore || 0);
    const styles = {
      border: `2px solid ${color || 'black'}`,
      backgroundColor: color || 'black',
    };
    return (
      <div
        className={classes.scoreContainer}
        style={styles}
        onClick={handleClickSmartGovernance}
      >
        <span style={{fontSize: 40, lineHeight: 2 }}>
          {smartGovernanceScore.toString()}
        </span>
      </div>
    );
  };

  const renderScoreBox = () => {
    const smartGovernanceStatus = getSmartGovernanceStatus();
    switch(smartGovernanceStatus) {
      case ESmartGovernanceStatus.inProgress:
        return renderInProgress();
      case ESmartGovernanceStatus.completed:
        return renderCompleted();
      case ESmartGovernanceStatus.notStarted:
        return renderNotStarted();
      case ESmartGovernanceStatus.assignError:
      case ESmartGovernanceStatus.scoreError:
      default:
        return renderReadonly();
    }
  };

  return (
    <Root>
      {renderScoreBox()}
      <div><b>Status:</b></div>
      <div>{getSmartGovernanceStatus() || 'Not started'}</div>
    </Root>
  );
}
